// useIcpTopUp.ts — ICP -> Cycles in-admin top-up flow (Atlas, 2026-05-22).
//
// Two-step flow:
//   1. Render the AccountIdentifier returned by get_icp_topup_account_id so
//      Jerry can send ICP to it from his NNS account (out-of-band).
//   2. Jerry pastes the resulting block index. We dispatch notify_icp_topup
//      which calls the CMC; CMC verifies the transfer, burns the ICP, and
//      mints cycles directly into dominus_admin.
//
// Reads: get_icp_topup_account_id() (query — fast, no cycles)
// Writes: notify_icp_topup(blockIndex) (update — admin-gated)
//
// The account ID is fetched once on mount and cached for the hook's
// lifetime. It is deterministic (derived from the canister's own principal)
// and never changes, so re-fetching is wasteful.
//
// Security: notify_icp_topup is admin-gated at the canister level. The
// frontend sends the II identity on every call; non-admin principals are
// rejected before any state is touched. (canister-security skill)

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Identity } from '@dfinity/agent';
import { createAdminAgent, createAdminActor } from '../lib/adminAgent';

export interface IcpTopUpResult {
  cyclesMinted: bigint;
}

export interface UseIcpTopUpReturn {
  /** The 64-char hex AccountIdentifier to send ICP to. null while fetching. */
  accountId: string | null;
  /** True while fetching the account id on mount. */
  isLoadingAccountId: boolean;
  /** Error message from the initial fetch, if any. */
  accountIdError: string | null;

  /** True while notify_icp_topup is in flight. */
  isMutating: boolean;
  /** Last successful notify result. Cleared on next attempt. */
  lastResult: IcpTopUpResult | null;
  /** Most recent notify error message. Cleared on next attempt. */
  error: string | null;

  /**
   * Dispatch notify_icp_topup with the given block index. Returns true on
   * success, false on failure. The hook surfaces the error via the `error`
   * state regardless of return value.
   */
  triggerNotify: (blockIndex: bigint) => Promise<boolean>;

  /** Re-fetch the account id (useful if the canister redeploy changes id). */
  refreshAccountId: () => Promise<void>;
}

export function useIcpTopUp(identity: Identity | null): UseIcpTopUpReturn {
  const [accountId, setAccountId] = useState<string | null>(null);
  const [isLoadingAccountId, setIsLoadingAccountId] = useState(false);
  const [accountIdError, setAccountIdError] = useState<string | null>(null);

  const [isMutating, setIsMutating] = useState(false);
  const [lastResult, setLastResult] = useState<IcpTopUpResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isMountedRef = useRef(true);

  const fetchAccountId = useCallback(async () => {
    if (!identity) return;
    setIsLoadingAccountId(true);
    setAccountIdError(null);
    try {
      const agent = await createAdminAgent(identity);
      const actor = createAdminActor(agent);
      const id = await actor.get_icp_topup_account_id();
      if (isMountedRef.current) setAccountId(id);
    } catch (err) {
      if (isMountedRef.current) {
        const msg = err instanceof Error ? err.message : 'Failed to fetch account ID.';
        setAccountIdError(msg);
      }
    } finally {
      if (isMountedRef.current) setIsLoadingAccountId(false);
    }
  }, [identity]);

  const refreshAccountId = useCallback(async () => {
    await fetchAccountId();
  }, [fetchAccountId]);

  useEffect(() => {
    isMountedRef.current = true;
    if (identity) {
      fetchAccountId();
    }
    return () => {
      isMountedRef.current = false;
    };
  }, [identity, fetchAccountId]);

  const triggerNotify = useCallback(
    async (blockIndex: bigint): Promise<boolean> => {
      if (!identity) {
        setError('No admin identity. Sign in first.');
        return false;
      }
      setIsMutating(true);
      setError(null);
      setLastResult(null);
      try {
        const agent = await createAdminAgent(identity);
        const actor = createAdminActor(agent);
        const result = await actor.notify_icp_topup(blockIndex);
        if ('err' in result) {
          if (isMountedRef.current) setError(result.err);
          return false;
        }
        if (isMountedRef.current) {
          setLastResult({ cyclesMinted: result.ok });
        }
        return true;
      } catch (err) {
        if (isMountedRef.current) {
          const msg = err instanceof Error ? err.message : 'notify_icp_topup failed.';
          setError(msg);
        }
        return false;
      } finally {
        if (isMountedRef.current) setIsMutating(false);
      }
    },
    [identity],
  );

  return {
    accountId,
    isLoadingAccountId,
    accountIdError,
    isMutating,
    lastResult,
    error,
    triggerNotify,
    refreshAccountId,
  };
}
